/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Switch, Route, useParams } from 'react-router-dom';
import { AppLayout } from '../../layout/AppLayout';
import { AppRouteInterface, HydratedRouteInterface } from '../../types/routes';
import Header from '@features/header';
import { AppHeader } from '@features/header/AppHeader';
import { NotFound } from '../not-found/NotFound';
import { BlockingLoader } from '@g17eco/atoms/loader';
import Dashboard, { DashboardRow } from '@g17eco/molecules/dashboard';
import { useGetAppConfigQuery } from '@api/g17ecoApi';
import { getAppRoute } from '../util';
import { SGXESGenomeOnboarding } from '@features/onboarding-esgenome';
import { Onboarding } from '@features/onboarding';
import { Activation } from '@features/onboarding/Activation';
import { BasicAlert } from '@g17eco/molecules/alert';
import { RouteContainer } from '@g17eco/molecules/route-container';
import { onboardingAppName, onboardingAppIcon, companyTrackerLightOnboardingActivationRoutes, companyTrackerLightOnboardingRoutes } from './routes';
import { getHydratedRoutes } from '../../constants/routeComponents';

export const onboardingRouteEntry: HydratedRouteInterface = {
  id: 'company_tracker_light_entry',
  label: onboardingAppName,
  path: '/:onboardingPath/onboarding',
  exact: false,
  component: CompanyTrackerLightRoute
}

const onboardingAppRoute: AppRouteInterface = {
  appName: onboardingRouteEntry.label,
  appIcon: onboardingAppIcon,
  routes: [companyTrackerLightOnboardingRoutes.COMPANY_TRACKER_ONBOARDING],
  hasSidebar: false
}

const onboardingRoutes = getHydratedRoutes(companyTrackerLightOnboardingRoutes);
const activationRoutes = getHydratedRoutes(companyTrackerLightOnboardingActivationRoutes);

function CompanyTrackerLightRoute() {
  const { onboardingPath } = useParams<{ onboardingPath: string }>();
  const { isError, error, isSuccess, data } = useGetAppConfigQuery(onboardingPath)

  if (isError) {
    return (
      <RouteContainer>
        <Dashboard>
          <DashboardRow>
            <BasicAlert className='w-100 text-center fs-5 p-4' type={'danger'}>
              {error.message}
            </BasicAlert>
          </DashboardRow>
        </Dashboard>
      </RouteContainer>
    )
  }

  if (!isSuccess) {
    return (
      <RouteContainer>
        <BlockingLoader />
      </RouteContainer>
    )
  }

  const appRoute = getAppRoute(onboardingAppRoute, data.appConfig)
  const onboardingRoute = { ...onboardingRoutes.COMPANY_TRACKER_ONBOARDING, label: appRoute.appName }

  const header = <Header brandingLogo={appRoute.headerIcon}>
    <AppHeader appRoute={appRoute} route={onboardingRoute} />
  </Header>;

  if (!data.appConfig) {
    return <BlockingLoader />;
  }

  // Manually wrap with container, need to be outside due to non-activated flow
  return (
    <RouteContainer>
      <AppLayout Header={header}>
        <Switch>
          {/* ESGenome */}
          <Route {...activationRoutes.ESGENOME_ONBOARDING_ACTIVATION} >
            <SGXESGenomeOnboarding appConfig={data.appConfig} />
          </Route>
          <Route {...onboardingRoutes.ESGENOME_ONBOARDING}>
            <SGXESGenomeOnboarding appConfig={data.appConfig} />
          </Route>
          {/* Materiality Tracker */}
          <Route {...activationRoutes.MATERIALITY_TRACKER_ONBOARDING_ACTIVATION} >
            <Activation appConfig={data.appConfig} />
          </Route>
          <Route {...onboardingRoutes.MATERIALITY_TRACKER_ONBOARDING}>
            <Onboarding appConfig={data.appConfig} />
          </Route>
          {/* Company Tracker */}
          <Route {...activationRoutes.COMPANY_TRACKER_ONBOARDING_ACTIVATION} >
            <Activation appConfig={data.appConfig} />
          </Route>
          <Route {...onboardingRoutes.COMPANY_TRACKER_ONBOARDING}>
            <Onboarding appConfig={data.appConfig} />
          </Route>
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </AppLayout>
    </RouteContainer>
  )
}
